/**
 * DrawdownEditor — inline percent editor for daily OR max drawdown. Same UX
 * as ProfitTargetEditor. `mode="daily"` edits daily_dd_pct (0.5-50%),
 * `mode="max"` edits max_dd_pct (1-90%).
 */
import { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BOUNDS = {
    daily: { key: "daily_dd_pct", min: 0.5, max: 50,  step: 0.5, label: "Daily DD" },
    max:   { key: "max_dd_pct",   min: 1,   max: 90,  step: 0.5, label: "Max DD" },
};

export default function DrawdownEditor({ accountId, currentPct, mode = "daily", onSaved }) {
    const cfg = BOUNDS[mode];
    const [editing, setEditing] = useState(false);
    const [pct, setPct]         = useState(Number(currentPct || (mode === "daily" ? 5 : 10)).toFixed(1));
    const [busy, setBusy]       = useState(false);

    async function save() {
        const parsed = Number(pct);
        if (!Number.isFinite(parsed) || parsed < cfg.min || parsed > cfg.max) {
            toast.error(`Enter a ${cfg.label.toLowerCase()} between ${cfg.min}% and ${cfg.max}%.`);
            return;
        }
        setBusy(true);
        try {
            await api.patch(`/accounts/${accountId}/drawdown-limits`, { [cfg.key]: parsed });
            toast.success(`${cfg.label} set to ${parsed}%`);
            setEditing(false);
            if (onSaved) onSaved(parsed);
        } catch (e) {
            toast.error(e.response?.data?.detail || `Couldn't save ${cfg.label.toLowerCase()}`);
        } finally { setBusy(false); }
    }

    if (!editing) {
        return (
            <button
                type="button"
                onClick={() => setEditing(true)}
                data-testid={`dd-edit-${mode}`}
                className="ml-2 text-[10px] uppercase tracking-widest font-extrabold text-primary hover:text-primary/80 inline-flex items-center gap-1"
                title={`Change your ${cfg.label.toLowerCase()} limit`}
            >
                <Pencil className="w-3 h-3" /> Edit
            </button>
        );
    }
    return (
        <div className="ml-2 inline-flex items-center gap-1" data-testid={`dd-editor-${mode}`}>
            <input
                type="number"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                min={cfg.min} max={cfg.max} step={cfg.step}
                className="w-14 h-6 text-xs mono border border-border rounded px-1 bg-background"
                data-testid={`dd-pct-input-${mode}`}
            />
            <span className="text-[10px] text-muted-foreground">%</span>
            <button onClick={save} disabled={busy} className="text-emerald-600 dark:text-emerald-400 disabled:opacity-50" data-testid={`dd-save-${mode}`}>
                <Check className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setEditing(false)} className="text-muted-foreground hover:text-foreground" data-testid={`dd-cancel-${mode}`}>
                <X className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}
